import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  verifyCredentials,
  getAccessToken,
  setAccessToken,
  getUsername,
  setUsername,
  getRole,
  setRole,
  clearAccessToken,
  onSessionEnded,
  type SessionRole,
} from '../core/index.js'
import { clearApiCalls } from '../core/index.js'
import { clearDemoSessionInfo, getDemoSessionInfo } from './demoSession.js'

/** How a demo session records an opener who is a SeeWhoCares admin (`opened_by` is `admin:<username>`), and where the admin area lives. */
const ADMIN_OPENER_PREFIX = 'admin:'
const ADMIN_PATH = '/admin'
/** Where this tab remembers why its session ended, so a reload still shows the right sign-in screen rather than a bare form. */
const SESSION_ENDED_KEY = 'autonome-web:sessionEnded'

function readSessionEnded(): SessionEnded | null {
  try {
    const raw = sessionStorage.getItem(SESSION_ENDED_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<SessionEnded>
    return { demo: parsed.demo === true, openedByAdmin: parsed.openedByAdmin === true }
  } catch {
    return null
  }
}

function storeSessionEnded(ended: SessionEnded | null): void {
  try {
    if (ended) {
      sessionStorage.setItem(SESSION_ENDED_KEY, JSON.stringify(ended))
    } else {
      sessionStorage.removeItem(SESSION_ENDED_KEY)
    }
  } catch {
    // sessionStorage unavailable: the notice lasts until the next reload only.
  }
}

/** Why the reader is looking at the sign-in page without having signed out: their session stopped being accepted. */
export interface SessionEnded {
  /** A demo session (opened without a password, for an hour) rather than a sign-in of their own. */
  demo: boolean
  /** A demo session a SeeWhoCares admin opened, which is opened again from the admin area. */
  openedByAdmin: boolean
}

interface AuthContextValue {
  isAuthenticated: boolean
  isVerifying: boolean
  error: string | null
  username: string | null
  role: SessionRole | null
  /** Set when the API stopped accepting the session; cleared by the next sign-in. */
  sessionEnded: SessionEnded | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Every answer cached for one sign-in is forgotten when it ends or another begins (26 October 2026): the cache is keyed
  // by facility and worker, not client, and demo clients share ids (north-campus, demo-wkr-001), so signing in as another
  // client in the same tab showed the last one's data - and kept it wherever the new client's read was refused.
  const queryClient = useQueryClient()
  const [isAuthenticated, setIsAuthenticated] = useState(() => getAccessToken() !== null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [username, setUsernameState] = useState(() => getUsername())
  const [role, setRoleState] = useState(() => getRole())
  // Only meaningful while signed out: a tab that is signed in again has nothing to explain.
  const [sessionEnded, setSessionEndedState] = useState<SessionEnded | null>(() => (getAccessToken() === null ? readSessionEnded() : null))

  function setSessionEnded(ended: SessionEnded | null): void {
    storeSessionEnded(ended)
    setSessionEndedState(ended)
  }

  // A 401 anywhere ends the session for the whole tab: the shell falls back to the sign-in page, which says why.
  useEffect(
    () =>
      onSessionEnded(() => {
        const demo = getDemoSessionInfo()
        clearDemoSessionInfo()
        clearApiCalls()
        queryClient.clear()
        setSessionEnded({ demo: demo !== null, openedByAdmin: demo?.openedBy.startsWith(ADMIN_OPENER_PREFIX) ?? false })
        setUsernameState(null)
        setRoleState(null)
        setIsAuthenticated(false)
      }),
    [queryClient],
  )

  async function login(username: string, password: string): Promise<void> {
    setIsVerifying(true)
    setError(null)
    try {
      const { accessToken, role } = await verifyCredentials(username, password)
      queryClient.clear()
      setAccessToken(accessToken)
      setUsername(username)
      setUsernameState(username)
      setRole(role)
      setRoleState(role)
      setIsAuthenticated(true)
      setSessionEnded(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed')
    } finally {
      setIsVerifying(false)
    }
  }

  function logout(): void {
    // A demo session an admin opened belongs to the admin area: signing out of it goes to the admin sign-in, not the operator's.
    const wasOpenedByAdmin = getDemoSessionInfo()?.openedBy.startsWith(ADMIN_OPENER_PREFIX) ?? false
    clearAccessToken()
    clearDemoSessionInfo()
    clearApiCalls()
    queryClient.clear()
    setSessionEnded(null)
    if (wasOpenedByAdmin) {
      window.location.assign(ADMIN_PATH)
      return
    }
    setUsernameState(null)
    setRoleState(null)
    setIsAuthenticated(false)
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isVerifying, error, username, role, sessionEnded, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
